import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import apiClient from '@lib/api/client';

// ── Client project page (in-the-loop pass) ─────────────────────────────────
// One place a homeowner can open to see everything the office has approved
// for them: what the project is, who's coming when, daily progress notes and
// site photos. Only office-shared logs/photos ever reach this page — the
// server filters on visible_to_client.

interface JobCore {
  id: number; title: string; status: string;
  site_address: string | null; site_city: string | null;
  start_date: string | null; end_date: string | null;
  description: string | null; client_notes: string | null;
}
interface Visit { id: number; scheduled_date: string; end_date: string | null; start_time: string | null; end_time: string | null; status: string }
interface LogRow {
  id: number; log_date: string; weather: string; temperature: number;
  crew_size: number; hours_worked: number; work_performed: string; materials_used: string | null;
}
interface PhotoRow { id: number; url: string; caption: string | null; milestone: string | null; photo_date: string }
// Office-shared steps (schedule_subtasks with visible_to_client = 1) — the
// server already filters these for clients, so anything here is safe to show.
// CR #154: rows can also be the client's OWN suggestions (pending/rejected),
// carrying the office's reply note; the server only sends those to their owner.
interface StepRow {
  id: number; title: string; status: string; description: string | null; start_date: string | null;
  approval_status?: 'approved' | 'pending' | 'rejected';
  suggested_by?: number | null;
  approval_note?: string | null;
}

const STEP_STATE: Record<string, { label: string; icon: string; cls: string }> = {
  completed:   { label: 'Done',      icon: 'check_circle',  cls: 'text-green-600' },
  in_progress: { label: 'Under way', icon: 'pending',       cls: 'text-blue-600' },
  blocked:     { label: 'On hold',   icon: 'pause_circle',  cls: 'text-amber-600' },
  pending:     { label: 'To come',   icon: 'radio_button_unchecked', cls: 'text-gray-300' },
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Getting ready', scheduled: 'Scheduled', in_progress: 'In progress',
  on_hold: 'Paused', completed: 'Complete', invoiced: 'Complete',
};
const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700', scheduled: 'bg-sky-100 text-sky-700',
  in_progress: 'bg-blue-100 text-blue-700', on_hold: 'bg-gray-100 text-gray-600',
  completed: 'bg-green-100 text-green-700', invoiced: 'bg-green-100 text-green-700',
};

const parseDateLocal = (d: string) => {
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day);
};
const fmtLong = (d: string) =>
  parseDateLocal(d).toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' });
const fmtShort = (d: string) =>
  parseDateLocal(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
const fmtTime = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

export const ClientJobDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobCore | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [docCount, setDocCount] = useState(0);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState<PhotoRow | null>(null);
  // CR #154 — "Ask us to add something" composer
  const [askOpen, setAskOpen] = useState(false);
  const [askTitle, setAskTitle] = useState('');
  const [askDetails, setAskDetails] = useState('');
  const [askBusy, setAskBusy] = useState(false);
  const [askSent, setAskSent] = useState(false);
  const [askError, setAskError] = useState('');

  const loadDetail = (alive?: () => boolean) =>
    Promise.allSettled([
      apiClient.get(`/portal/me/jobs/${id}`),
      apiClient.get(`/portal/me/jobs/${id}/updates`),
    ]).then(([detail, updates]) => {
      if (alive && !alive()) return;
      if (detail.status === 'fulfilled') {
        const d = detail.value.data.data;
        setJob(d.job);
        setVisits(d.schedule || []);
        setSteps(d.tasks || []);
        setDocCount((d.documents || []).length);
      } else {
        setError('We couldn\'t find that project.');
      }
      if (updates.status === 'fulfilled') {
        setLogs(updates.value.data.data.logs || []);
        setPhotos(updates.value.data.data.photos || []);
      }
      setLoading(false);
    });

  useEffect(() => {
    let alive = true;
    loadDetail(() => alive);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // CR #154 — send the request; it lands on the office board as pending.
  const sendAsk = async () => {
    if (!askTitle.trim()) return;
    setAskBusy(true);
    setAskError('');
    try {
      await apiClient.post(`/portal/me/jobs/${id}/suggest-task`, {
        title: askTitle.trim(),
        description: askDetails.trim() || undefined,
      });
      setAskSent(true);
      setAskOpen(false);
      setAskTitle('');
      setAskDetails('');
      await loadDetail();
    } catch {
      setAskError('We couldn\'t send that just now — please try again, or give us a call.');
    } finally {
      setAskBusy(false);
    }
  };

  // Merge shared logs + photos into one newest-first timeline, grouped by day.
  const timeline = useMemo(() => {
    const byDate = new Map<string, { log?: LogRow; photos: PhotoRow[] }>();
    for (const l of logs) {
      const k = String(l.log_date).slice(0, 10);
      if (!byDate.has(k)) byDate.set(k, { photos: [] });
      byDate.get(k)!.log = l;
    }
    for (const p of photos) {
      const k = String(p.photo_date).slice(0, 10);
      if (!byDate.has(k)) byDate.set(k, { photos: [] });
      byDate.get(k)!.photos.push(p);
    }
    return [...byDate.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [logs, photos]);

  // CR #154 — split the office's approved steps from the client's own requests
  // (pending = waiting on the office, rejected = declined, with any reply note).
  const approvedSteps = steps.filter(s => !s.approval_status || s.approval_status === 'approved');
  const myRequests = steps.filter(s => s.approval_status === 'pending' || s.approval_status === 'rejected');

  // CR #135 — a real progress figure: shared steps finished ÷ shared steps total.
  // Only approved steps count — a request that's still waiting isn't work yet.
  const stepsDone = approvedSteps.filter(s => s.status === 'completed').length;
  const stepsActive = approvedSteps.filter(s => s.status === 'in_progress').length;
  const pct = approvedSteps.length > 0 ? Math.round((stepsDone / approvedSteps.length) * 100) : null;

  const todayKey = new Date().toISOString().slice(0, 10);
  const upcomingVisits = visits
    .filter(v => v.status !== 'cancelled' && String(v.scheduled_date).slice(0, 10) >= todayKey)
    .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)))
    .slice(0, 3);

  if (loading) return (
    <div className="space-y-3 pt-2">
      <div className="h-32 rounded-2xl bg-gray-100 animate-pulse" />
      <div className="h-64 rounded-2xl bg-gray-100 animate-pulse" />
    </div>
  );
  if (error || !job) return (
    <div className="pt-8 text-center space-y-3">
      <span className="material-symbols-outlined text-gray-300 text-4xl">home_work</span>
      <p className="text-base text-gray-500">{error || 'Project not found.'}</p>
      <button onClick={() => navigate('/portal/client')} className="text-base font-bold text-[#C77D3F]">← Back home</button>
    </div>
  );

  return (
    <div className="space-y-4 pb-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 pt-1 text-base font-bold text-[#C77D3F]">
        <span className="material-symbols-outlined text-lg">arrow_back_ios</span> Back
      </button>

      {/* Project header */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-2xl font-black text-[#2C1810] leading-tight">{job.title}</h1>
          <span className={`flex-shrink-0 text-sm px-3 py-1 rounded-full font-bold ${STATUS_COLOR[job.status] || 'bg-gray-100 text-gray-600'}`}>
            {STATUS_LABEL[job.status] || job.status}
          </span>
        </div>
        {(job.start_date || job.end_date) && (
          <p className="text-sm text-gray-500 mt-2">
            {job.start_date && <>Started {fmtShort(job.start_date)}</>}
            {job.start_date && job.end_date && ' · '}
            {job.end_date && <>Planned finish {fmtShort(job.end_date)}</>}
          </p>
        )}
        {job.site_address && (
          <button type="button"
            onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(job.site_address!)}`, '_blank')}
            className="mt-3 flex items-center gap-2 text-base text-[#C77D3F] font-semibold active:opacity-70">
            <span className="material-symbols-outlined">location_on</span>
            {job.site_address}
          </button>
        )}
        {(job.client_notes || job.description) && (
          <p className="text-sm text-gray-600 mt-3 whitespace-pre-wrap border-t border-gray-100 pt-3">
            {job.client_notes || job.description}
          </p>
        )}
      </div>

      {/* How far along we are — the steps the office has shared with you */}
      {pct !== null && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-base font-black text-[#2C1810]">How far along we are</p>
            <p className="text-2xl font-black text-[#C77D3F] leading-none">{pct}%</p>
          </div>
          <div className="h-3 w-full rounded-full bg-gray-100 overflow-hidden mt-3">
            <div className="h-full rounded-full bg-[#C77D3F] transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {stepsDone} of {steps.length} steps finished{stepsActive > 0 ? `, ${stepsActive} under way right now` : ''}.
          </p>
          {photos.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              {photos.length} site photo{photos.length === 1 ? '' : 's'} shared with you — scroll down to see them.
            </p>
          )}

          <div className="mt-4 divide-y divide-gray-50">
            {approvedSteps.map(s => {
              const st = STEP_STATE[s.status] || STEP_STATE.pending;
              return (
                <div key={s.id} className="flex items-start gap-3 py-2.5">
                  <span className={`material-symbols-outlined text-xl mt-0.5 ${st.cls}`}>{st.icon}</span>
                  <div className="min-w-0 flex-1">
                    <p className={`text-[15px] ${s.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900 font-medium'}`}>
                      {s.title}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {st.label}{s.start_date ? ` · planned for ${fmtShort(s.start_date)}` : ''}
                      {s.suggested_by ? ' · you asked for this one' : ''}
                    </p>
                    {s.suggested_by && s.approval_note && (
                      <p className="text-xs text-gray-600 mt-1 bg-[#FAF6F0] rounded-lg px-2.5 py-1.5">
                        <span className="font-semibold">From our office:</span> {s.approval_note}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            These are the steps our office has shared with you. We tick them off as the crew finishes them.
          </p>
        </div>
      )}

      {/* CR #154 — ask the office to add something, and follow your requests */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <p className="text-base font-black text-[#2C1810]">Want something added?</p>
        <p className="text-sm text-gray-500 mt-1">
          Tell us what you'd like done and we'll review it. Nothing is added to the
          plan until we approve it.
        </p>

        {/* Requests already sent */}
        {myRequests.length > 0 && (
          <div className="mt-3 space-y-2.5">
            {myRequests.map(s => (
              <div key={s.id} className="rounded-xl border border-gray-100 bg-[#FAF6F0]/60 p-3">
                <div className="flex items-start gap-2.5">
                  <span className={`material-symbols-outlined text-xl mt-0.5 ${s.approval_status === 'pending' ? 'text-amber-500' : 'text-gray-400'}`}>
                    {s.approval_status === 'pending' ? 'hourglass_top' : 'block'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-medium text-gray-900">{s.title}</p>
                    <p className={`text-xs font-semibold mt-0.5 ${s.approval_status === 'pending' ? 'text-amber-600' : 'text-gray-500'}`}>
                      {s.approval_status === 'pending' ? 'Waiting for our approval' : 'We couldn\'t add this one'}
                    </p>
                    {s.approval_note && (
                      <p className="text-xs text-gray-600 mt-1.5 bg-white rounded-lg px-2.5 py-1.5 border border-gray-100">
                        <span className="font-semibold">From our office:</span> {s.approval_note}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {askSent && !askOpen && (
          <p className="mt-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">check_circle</span>
            Sent! We'll review it and you'll see our answer right here.
          </p>
        )}

        {askOpen ? (
          <div className="mt-3 space-y-2.5">
            {askError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{askError}</p>
            )}
            <input
              value={askTitle}
              onChange={e => setAskTitle(e.target.value)}
              placeholder="e.g. Add a shelf in the hall closet"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40"
            />
            <textarea
              value={askDetails}
              onChange={e => setAskDetails(e.target.value)}
              rows={3}
              placeholder="Any details that would help us (optional)"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40"
            />
            <div className="flex gap-2">
              <button onClick={() => { setAskOpen(false); setAskError(''); }}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-base font-semibold text-gray-600">
                Cancel
              </button>
              <button onClick={sendAsk} disabled={askBusy || !askTitle.trim()}
                className="flex-1 py-3 rounded-xl bg-[#C77D3F] text-white text-base font-bold disabled:opacity-40 active:scale-[0.99]">
                {askBusy ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => { setAskOpen(true); setAskSent(false); }}
            className="mt-3 w-full py-3 rounded-xl border-2 border-[#C77D3F] text-[#C77D3F] text-base font-bold flex items-center justify-center gap-2 active:bg-[#C77D3F]/5">
            <span className="material-symbols-outlined">add_circle</span>
            Ask us to add something
          </button>
        )}
      </div>

      {/* Next visits */}
      {upcomingVisits.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <p className="text-base font-black text-[#2C1810] mb-2">Next crew visits</p>
          {upcomingVisits.map(v => (
            <div key={v.id} className="flex items-center gap-3 py-2">
              <span className="material-symbols-outlined text-[#C77D3F]">event</span>
              <p className="text-base text-gray-800">
                <span className="font-semibold">{fmtLong(v.scheduled_date)}</span>
                {v.start_time && <span className="text-gray-500"> · from {fmtTime(v.start_time)}</span>}
              </p>
            </div>
          ))}
          <Link to="/portal/client/schedule" className="mt-1 inline-flex items-center gap-1 text-base font-bold text-[#C77D3F]">
            Open the full calendar <span className="material-symbols-outlined text-base">arrow_forward</span>
          </Link>
        </div>
      )}

      {/* Documents shortcut */}
      {docCount > 0 && (
        <Link to="/portal/client/documents"
          className="flex items-center gap-3 bg-white rounded-2xl border border-gray-200 p-5 active:bg-[#FAF6F0]">
          <span className="material-symbols-outlined text-[#C77D3F]">folder</span>
          <div className="flex-1">
            <p className="text-base font-bold text-gray-900">Your documents</p>
            <p className="text-sm text-gray-500">{docCount} file{docCount === 1 ? '' : 's'} shared with you</p>
          </div>
          <span className="material-symbols-outlined text-gray-300">chevron_right</span>
        </Link>
      )}

      {/* Progress timeline */}
      <div>
        <p className="text-base font-black text-[#2C1810] px-1 mb-2">Progress updates</p>
        {timeline.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl">photo_camera</span>
            <p className="text-base font-semibold text-gray-600 mt-3">No updates posted yet</p>
            <p className="text-sm text-gray-400 mt-1">As work happens, we'll post progress notes and site photos here so you can follow along.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {timeline.map(([date, entry]) => (
              <div key={date} className="bg-white rounded-2xl border border-gray-200 p-5">
                <p className="text-base font-black text-[#2C1810]">{fmtLong(date)}</p>
                {entry.log && (
                  <>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FAF6F0] text-[#8B5A2B]">
                        {entry.log.weather} · {entry.log.temperature}°C
                      </span>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FAF6F0] text-[#8B5A2B]">
                        {entry.log.crew_size} {Number(entry.log.crew_size) === 1 ? 'person' : 'people'} on site
                      </span>
                    </div>
                    <p className="text-[15px] text-gray-800 mt-3 whitespace-pre-wrap leading-relaxed">{entry.log.work_performed}</p>
                    {entry.log.materials_used && (
                      <p className="text-sm text-gray-500 mt-2"><span className="font-semibold">Materials:</span> {entry.log.materials_used}</p>
                    )}
                  </>
                )}
                {entry.photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-1.5 mt-3">
                    {entry.photos.map(p => (
                      <button key={p.id} type="button" onClick={() => setLightbox(p)}
                        className="aspect-square rounded-xl overflow-hidden bg-gray-100 active:opacity-80">
                        <img src={p.url} alt={p.caption || 'Site photo'} loading="lazy" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photo lightbox — one tap in, one tap out */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" onClick={() => setLightbox(null)}>
          <div className="flex justify-end p-4">
            <button className="w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center" aria-label="Close photo">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center px-2 pb-6 min-h-0">
            <img src={lightbox.url} alt={lightbox.caption || 'Site photo'} className="max-w-full max-h-full object-contain rounded-lg" />
          </div>
          {(lightbox.caption || lightbox.milestone) && (
            <p className="text-white text-center text-base px-6 pb-8">{lightbox.caption || lightbox.milestone}</p>
          )}
        </div>
      )}
    </div>
  );
};
